import { useAppSelector } from "../../../app/hooks";
import { selectConfig } from "../../config/ConfigSlice";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  generateDateRange,
  parseSeatClassOverrides,
  getClassLabel,
} from "../utils";
import { getLocale } from "../../../utils";
import AirlineLogo from "./AirlineLogo";
import { FlightDetails } from "../../../types";

interface Props {
  flightDetails1: FlightDetails;
  flightDetails2: FlightDetails;
  flightDetails3: FlightDetails;
  dateMin: Date;
  dateMax: Date;
}

function SearchFlightsResultModalRange({
  flightDetails1,
  flightDetails2,
  flightDetails3,
  dateMin,
  dateMax,
}: Props) {
  return (
    <>
      <Details flight={flightDetails1} dateMin={dateMin} dateMax={dateMax} />
      {flightDetails2 && (
        <Details flight={flightDetails2} dateMin={dateMin} dateMax={dateMax} />
      )}
      {flightDetails3 && (
        <Details flight={flightDetails3} dateMin={dateMin} dateMax={dateMax} />
      )}
    </>
  );
}

function Details({ flight, dateMin, dateMax }) {
  const { t } = useTranslation();
  const config = useAppSelector(selectConfig);
  const locale = getLocale(config.app.language);

  const [dates, setDates] = useState([]);

  const isArabic = config.app.language === "ar";

  const showFirstClass = useAppSelector(
    (state) => state.config.components.Results.showFirstClass
  );
  const seatClassOverrides = parseSeatClassOverrides(
    config.components.Results.translateSeatClass
  );
  const seatClassOverridesNew = parseSeatClassOverrides(
    config.components.Results.translateSeatClassNew
  );
  const transitionDate = config.mapper.bookingLink.transitionDate;

  useEffect(() => {
    if (dateMin && dateMax && flight) {
      setDates(
        generateDateRange(
          flight.effectiveFrom,
          flight.effectiveTo,
          dateMin,
          dateMax,
          flight.frequency
        )
      );
    } else {
      setDates([]);
    }
  }, [flight, dateMin, dateMax]);

  if (dates.length === 0) {
    return null;
  }

  return (
    <>
      <div className="field is-horizontal">
        <div className="field-label is-normal">
          <AirlineLogo airlineCode={flight.operatedBy} />
        </div>
        <div className="field-body">
          <div className="content">
            {`${flight.airline} ${flight.operatedBy} ${flight.flightNo}`}
            {flight.oagOpByCarrier && (
              <p className="is-size-7">{`${t("LABEL_OPERATED_BY")} ${
                flight.sadName
              } - ${flight.oagOpByCarrier} ${flight.oagOpByFlightNo}`}</p>
            )}
          </div>
        </div>
      </div>
      <div className="field is-horizontal">
        <div className="field-label">
          <label className="label">{t("LABEL_FLIGHT_FINDER_FROM")}</label>
        </div>
        <div className="field-body">
          {flight.depart} - {flight.departPortName}{" "}
          {flight.departureTerminal && `(${flight.departureTerminal})`}
        </div>
      </div>
      <div className="field is-horizontal">
        <div className="field-label">
          <label className="label">{t("LABEL_FLIGHT_FINDER_TO")}</label>
        </div>
        <div className="field-body">
          {flight.arrival} - {flight.arrivalPortName}{" "}
          {flight.arrivalTerminal && `(${flight.arrivalTerminal})`}
        </div>
      </div>
      <div className="table-container">
        <table
          id="flight-details-table"
          className="table is-bordered is-fullwidth has-text-centered is-size-7 has-sticky-column"
        >
          <thead>
            <tr>
              <th />
              {dates.map((date, i) => {
                return (
                  <th key={i}>
                    {config.components.Results.gridHeadDateFormat[0] &&
                      format(
                        date.date,
                        isArabic
                          ? "EEEE"
                          : config.components.Results.gridHeadDateFormat[0],
                        { locale }
                      )}
                    <br />
                    {config.components.Results.gridHeadDateFormat[1] &&
                      format(
                        date.date,
                        config.components.Results.gridHeadDateFormat[1],
                        { locale }
                      )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>{t("LABEL_SORT_BY_TIME_DEPART")}</th>
              {dates.map((date, i) => {
                if (date.scheduled) {
                  return <td key={i}>{flight.departureTimeAsString}</td>;
                } else {
                  return <td key={i} className="has-background-grey-light" />;
                }
              })}
            </tr>
            <tr>
              <th>{t("LABEL_SORT_BY_TIME_ARRIVE")}</th>
              {dates.map((date, i) => {
                if (date.scheduled) {
                  return <td key={i}>{flight.arrivalTimeAsString}</td>;
                } else {
                  return <td key={i} className="has-background-grey-light" />;
                }
              })}
            </tr>
            <tr>
              <th>{t("LABEL_WHERE_WE_FLY_DURATION")}</th>
              {dates.map((date, i) => {
                if (date.scheduled) {
                  return (
                    <td key={i}>
                      {Math.floor(flight.travelTime / 60)}h{" "}
                      {flight.travelTime % 60}m
                    </td>
                  );
                } else {
                  return <td key={i} className="has-background-grey-light" />;
                }
              })}
            </tr>
            <tr>
              <th>{t("LABEL_FLIGHT_FINDER_CLASSES")}:</th>
              {dates.map((date, i) => {
                if (date.scheduled) {
                  return (
                    <td key={i}>
                      {showFirstClass && flight.classFirst !== null && (
                        <div>
                          {getClassLabel(
                            "CLASS_F",
                            date.date,
                            transitionDate,
                            seatClassOverrides,
                            seatClassOverridesNew,
                            t
                          )}
                        </div>
                      )}
                      {flight.classBusiness !== null && (
                        <div>{t("CLASS_B")}</div>
                      )}
                      {flight.classEconomy !== null && (
                        <div>
                          {getClassLabel(
                            "CLASS_E",
                            date.date,
                            transitionDate,
                            seatClassOverrides,
                            seatClassOverridesNew,
                            t
                          )}
                        </div>
                      )}
                    </td>
                  );
                } else {
                  return <td key={i} className="has-background-grey-light" />;
                }
              })}
            </tr>
            <tr>
              <th>{t("LABEL_AIRCRAFT")}:</th>
              {dates.map((date, i) => {
                if (date.scheduled) {
                  return <td key={i}>{flight.aircraftName}</td>;
                } else {
                  return <td key={i} className="has-background-grey-light" />;
                }
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

export default SearchFlightsResultModalRange;
